import {
  type ComponentProps,
  type ComponentType,
  createElement,
  lazy,
} from "react";

import { runWhenIdle, runWhenStartupSettled } from "./idle";

// If the tab never goes idle this long, warm the next chunk anyway — the point
// is that a funder's first visit ends with the complete app cached, so a
// perpetually-busy page must not starve the warm forever.
const IDLE_BACKSTOP_MS = 10_000;

type BackgroundWarmOptions = {
  /**
   * Consulted when the warm reaches this chunk (not at registration, so state
   * written during startup — e.g. the cached identity — is available). Return
   * false to leave the chunk cold; it still loads on demand through `lazy()`
   * if its route is visited. For pages whose route is role-gated: warming
   * them for everyone spends the first visit's bandwidth and main-thread
   * evaluate time on a page most users can never open. Skipped chunks
   * deliberately give up the warm's offline coverage.
   */
  warmIf?: () => boolean;
};

type RegisteredChunk = {
  loader: () => Promise<unknown>;
  warmIf?: () => boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint
type BackgroundWarmComponent<T extends ComponentType<any>> = ComponentType<
  ComponentProps<T>
> & {
  /** Starts this component's import and fills its synchronous render cache. */
  preload: () => Promise<{ default: T }>;
};

const registeredChunks: Array<RegisteredChunk> = [];
let warmStarted = false;
let warmAborted = false;
let warmImportsInFlight = 0;

// vite:preloadError handshake with main.tsx. The handler preventDefault()s
// every event, which makes Vite's preload helper RESOLVE (with undefined)
// instead of rethrowing — so a suppressed warm failure never reaches the
// warm's own catch. It surfaces instead as an undefined RESULT in the warm's
// then-handler (a real module namespace is never undefined), which records
// it here; the handler's deferred decision claims one record per event.
let unclaimedWarmFailures = 0;

/**
 * True while a background warm `import()` is in flight — the only window in
 * which a vite:preloadError can be warm-owned. When false, a failure is a
 * user navigation's and the handler can decide synchronously.
 */
export function isWarmImportInFlight(): boolean {
  return warmImportsInFlight > 0;
}

/**
 * Claims one recorded warm-import failure, if any. The preload-error
 * handler calls this one macrotask after dispatch (the warm's promise has
 * settled by then): true means the event was the warm's own failure and
 * must not be answered with a reload; false means it was a concurrent user
 * navigation's, which takes the normal reload path. One claim per record,
 * so a warm failure and a user failure in the same window still reload
 * exactly once.
 */
export function claimWarmImportFailure(): boolean {
  if (unclaimedWarmFailures === 0) return false;
  unclaimedWarmFailures--;
  return true;
}

/**
 * Test-only: clears the chunk registry and every warm latch so a test can run
 * the warm against exactly the chunks it registers. The registry and latches
 * are module-global (one warm per page load), so without this a test process
 * inherits chunks registered by other suites' imports.
 */
export function resetBackgroundChunkWarmingForTests(): void {
  registeredChunks.length = 0;
  warmStarted = false;
  warmAborted = false;
  warmImportsInFlight = 0;
  unclaimedWarmFailures = 0;
}

/**
 * `React.lazy`-compatible component factory (same loader signature, plus warm
 * options) that also registers the chunk for background warming (see
 * `warmRegisteredChunksWhenIdle`). A successful warm stores the resolved
 * component as well as populating the browser's module cache, so its first
 * later render can bypass `React.lazy` and cannot flash a Suspense fallback.
 * Cold and failed imports still use `React.lazy` and retain its normal retry
 * and fallback behavior.
 */
/**
 * Register a plain (non-component) chunk loader for the idle background warm
 * — e.g. the markdown<->HTML converter the note editor imports on demand.
 * Warming matters most for OFFLINE coverage: an iPad that never touched the
 * surface while online must still find the chunk in cache, or the feature is
 * dead until connectivity returns.
 */
export function registerWarmChunk(
  loader: () => Promise<unknown>,
  options: BackgroundWarmOptions = {},
): void {
  registeredChunks.push({
    loader: loader as RegisteredChunk["loader"],
    warmIf: options.warmIf,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint
export function lazyWithBackgroundWarm<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  { warmIf }: BackgroundWarmOptions = {},
): BackgroundWarmComponent<T> {
  let resolvedModule: { default: T } | undefined;
  const trackedLoader = async () => {
    const loadedModule = await loader();
    resolvedModule = loadedModule;
    return loadedModule;
  };

  registeredChunks.push({ loader: trackedLoader, warmIf });
  const LazyComponent = lazy(trackedLoader);
  // Once any instance starts cold, keep using the same lazy element type for
  // this component. Its React.lazy payload becomes synchronous after the
  // import resolves, while switching to ResolvedComponent on a later parent
  // render could remount an already-committed subtree and discard local state.
  let lazyRenderSelected = false;

  const BackgroundWarmComponent = (props: ComponentProps<T>) => {
    if (!resolvedModule) lazyRenderSelected = true;
    const ResolvedComponent = resolvedModule?.default;
    return ResolvedComponent && !lazyRenderSelected
      ? createElement(ResolvedComponent, props)
      : createElement(LazyComponent, props);
  };

  return Object.assign(BackgroundWarmComponent, { preload: trackedLoader });
}

/**
 * Fetch and evaluate one chunk immediately, without waiting for startup to
 * settle. For a chunk that is about to be landing-visible (the funder
 * workspace's video player), the idle-time warm below loses the race with the
 * first render, which puts the chunk fetch on the page's critical path — call
 * this alongside the route's data prefetches instead. Loading through the
 * component's `preload` function means a successful warm also fills its
 * synchronous render cache, avoiding a one-frame Suspense fallback later.
 *
 * Shares the background warm's vite:preloadError handshake so a failed
 * speculative import is never answered with a page reload out from under the
 * user (see main.tsx's handler). Unlike the idle warm, a failure here does
 * not abort background warming — the idle pass discovers unfetchable chunks
 * itself, and this chunk still loads on demand through `lazy()` when its
 * surface renders.
 */
export function warmChunkNow(loader: () => Promise<unknown>): void {
  if (typeof window === "undefined") return;
  warmImportsInFlight++;
  void loader()
    // Dev serves unwrapped imports (no preload helper, no event), so a
    // failure can still arrive as a plain rejection: normalize it to the
    // undefined-result shape the prod path produces.
    .catch(() => undefined)
    .then((result) => {
      warmImportsInFlight--;
      if (result === undefined) unclaimedWarmFailures++;
    });
}

/**
 * Start a route chunk alongside app startup when the initial document URL
 * already targets it. This keeps a split page out of unrelated entry paths
 * without making a cold direct landing wait for React to reach its lazy
 * boundary before the request begins.
 */
export function warmChunkNowForInitialPath(
  loader: () => Promise<unknown>,
  matchesPath: (pathname: string) => boolean,
): void {
  // The string check, not a bare window guard: bun's shared DOM-less test
  // process can hold a PARTIAL window stub (a test file's module-level mock
  // with a location but no pathname), and this runs at module load — an
  // unguarded read handed the predicate `undefined` and crashed every
  // later-loaded test file importing a page module, in whatever file order
  // CI happened to pick. No pathname simply means no initial path to match.
  const pathname =
    typeof window === "undefined" ? undefined : window.location?.pathname;
  if (typeof pathname !== "string" || !matchesPath(pathname)) {
    return;
  }
  warmChunkNow(loader);
}

/**
 * Once startup has settled AND the main thread is idle, fetch and evaluate
 * every registered lazy chunk, one chunk per idle window, so the whole app is
 * cached after the very first page — without blocking startup. The
 * startup-settled gate (`load` + grace period + idle; see
 * `runWhenStartupSettled`) keeps the warm off the critical startup window:
 * `load` alone fires while round data and the video player are still coming
 * up, and idle callbacks scheduled then put every warm import's fetch and
 * main-thread evaluate right on that path. `runWhenIdle` between chunks
 * yields to any user interaction. The chunk registry is iterated live, so a
 * warmed chunk that registers lazy pages of its own gets those warmed too.
 */
export function warmRegisteredChunksWhenIdle(options?: {
  /** Overrides the startup grace period (tests only). */
  startupGraceMs?: number;
}): void {
  if (warmStarted || typeof window === "undefined") return;
  warmStarted = true;
  let index = 0;
  const warmNext = () => {
    if (warmAborted) return;
    if (!navigator.onLine) {
      // Offline: hold the warm until connectivity returns rather than burning
      // fetch failures (each would dispatch vite:preloadError).
      window.addEventListener(
        "online",
        () => runWhenIdle(warmNext, { timeout: IDLE_BACKSTOP_MS }),
        { once: true },
      );
      return;
    }
    let chunk = registeredChunks[index++];
    // Chunks whose warmIf gate declines are left cold (they load on demand);
    // skipping is free, so don't spend an idle window per skip.
    while (chunk?.warmIf && !chunk.warmIf()) {
      chunk = registeredChunks[index++];
    }
    if (!chunk) return;
    warmImportsInFlight++;
    void chunk
      .loader()
      // Dev serves unwrapped imports (no preload helper, no event), so a
      // failure can still arrive as a plain rejection: normalize it to the
      // undefined-result shape the prod path produces.
      .catch(() => undefined)
      .then((result) => {
        warmImportsInFlight--;
        if (result === undefined) {
          // This warm import failed (suppressed by the preload-error
          // handler, or a dev-mode rejection). Record it for the handler's
          // deferred claim and stop warming: if chunks are unfetchable
          // (mid-deploy stale hashes, dead network), each further attempt
          // would fail the same way. The next full page load warms fresh.
          unclaimedWarmFailures++;
          warmAborted = true;
          return;
        }
        if (!warmAborted) runWhenIdle(warmNext, { timeout: IDLE_BACKSTOP_MS });
      });
  };
  runWhenStartupSettled(warmNext, {
    graceMs: options?.startupGraceMs,
    timeout: IDLE_BACKSTOP_MS,
  });
}
